import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Lightning, WarningCircle } from "@phosphor-icons/react";

const PLAN_LABEL = { trial: "Free Trial", starter: "Starter", professional: "Professional", enterprise: "Enterprise", annual: "Annual" };

export default function UsageBanner({ compact = false }) {
  const { company } = useAuth();
  const navigate = useNavigate();
  const u = company?.usage;
  if (!u) return null;

  const unlimited = u.unlimited;
  const pct = unlimited ? 0 : (u.limit > 0 ? Math.min(100, Math.round((u.used / u.limit) * 100)) : 0);
  const atLimit = !unlimited && u.remaining <= 0;
  const warn = !unlimited && !atLimit && u.limit > 0 && pct >= 75;
  const barColor = atLimit ? "#DC2626" : warn ? "#D97706" : "#2563EB";
  const resets = u.period_end ? new Date(u.period_end).toLocaleDateString() : null;

  return (
    <div data-testid="usage-banner" className={`rounded-lg border ${atLimit ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"} ${compact ? "p-4" : "p-5"}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">
              {u.is_trial ? "Free Reviews" : "Current Plan"}
            </span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5" data-testid="usage-plan">
              {PLAN_LABEL[u.plan] || u.plan}
            </span>
          </div>
          <div className="font-display font-black text-2xl tracking-tighter text-[#0F172A] mt-1" data-testid="usage-count">
            {unlimited ? <>Unlimited <span className="text-sm font-semibold text-slate-400">this year</span></>
              : <>{u.used} <span className="text-slate-300">/</span> {u.limit} <span className="text-sm font-semibold text-slate-400">used</span></>}
            {!unlimited && u.extra_credits > 0 && <span className="ml-2 text-xs font-semibold text-emerald-600">+{u.extra_credits} credit{u.extra_credits !== 1 ? "s" : ""}</span>}
          </div>
          <div className="text-xs text-slate-400 mt-0.5" data-testid="usage-remaining">
            {unlimited ? `${u.used} analyzed${resets ? ` · renews ${resets}` : ""}`
              : <>{u.remaining} {u.is_trial ? "free review" : "proposal"}{u.remaining !== 1 ? "s" : ""} remaining{resets && !u.is_trial ? ` · resets ${resets}` : ""}</>}
          </div>
        </div>
        <button data-testid="usage-upgrade-btn" onClick={() => navigate("/pricing")}
          className="inline-flex items-center gap-2 h-10 px-4 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm transition-colors">
          <Lightning size={16} weight="fill" /> {u.is_trial ? "Choose a Plan" : "Upgrade Plan"}
        </button>
      </div>

      {!unlimited && (
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden mt-3">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: barColor }} />
        </div>
      )}

      {(warn || atLimit) && (
        <div className={`flex items-center gap-2 mt-3 text-xs font-medium ${atLimit ? "text-red-700" : "text-amber-700"}`} data-testid="usage-warning">
          <WarningCircle size={14} weight="bold" />
          {atLimit
            ? (u.is_trial ? "Your free reviews are complete. Choose a plan to keep analyzing." : "You've reached your monthly proposal allowance.")
            : pct >= 90 ? "You're almost at your monthly proposal limit." : "You've used 75% of your monthly proposal allowance."}
        </div>
      )}
    </div>
  );
}
